import React, { useEffect } from 'react';
import { useYoutubeIframeApiContext } from '../../context/YouTubeIframeApiProvider';
import { getVideoIdFromUrl } from '@jbhi-fi/utils/src/urls/getVideoIdFromUrl';
import { getRandomInt } from '@jbhi-fi/utils/src/random/random';

const YOUTUBE_VIDEO_PLAYER_OPTIONS = {
  enablejsapi: 1,
  origin: window.origin,
  rel: 0,
  playsinline: 1,
};

export type UseYoutubePlayerProps = {
  videoUrl: string;
  element: React.RefObject<HTMLDivElement>;
  onPlayerLoaded?: ({ id, player }: { id: string; player: YoutubeVideoPlayer }) => void;
  onStateChange?: (event: { data: number }) => void;
};

/* A hook to load a YouTube iFrame player using the iframe API.  The player is then added to the global context for control by other components */
export const useYoutubePlayer = ({ videoUrl, element, onPlayerLoaded, onStateChange }: UseYoutubePlayerProps) => {
  const { youTubeIframeApi, isReady: isYouTubeApiReady } = useYoutubeIframeApiContext();
  const videoId = getVideoIdFromUrl(videoUrl);

  useEffect(() => {
    if (!isYouTubeApiReady || !youTubeIframeApi?.Player || !element.current) return;

    if (!element.current.id) {
      const uniqueId = `${Date.now()}-${getRandomInt(1000)}`;
      element.current.id = `yt-element-${videoId}-${uniqueId}`;
    }

    const youTubePlayer = new youTubeIframeApi.Player(element.current.id, {
      playerVars: YOUTUBE_VIDEO_PLAYER_OPTIONS,

      videoId,
      events: {
        onReady: () => {
          const player = new YoutubeVideoPlayer(youTubePlayer);
          onPlayerLoaded?.({ id: videoUrl, player });
        },
        onStateChange: (event: { data: number }) => {
          onStateChange?.(event);
        },
      },
    });

    return () => {
      youTubePlayer.destroy();
    };
  }, [isYouTubeApiReady, youTubeIframeApi, element]);
};

export class YoutubeVideoPlayer {
  private _youTubePlayer: any;

  constructor(youTubePlayer: any) {
    this._youTubePlayer = youTubePlayer;
  }

  play() {
    this._youTubePlayer.playVideo();
  }

  pause() {
    return this._youTubePlayer.pauseVideo();
  }

  seek(seconds: number) {
    this._youTubePlayer.seekTo(seconds);
  }
}
